import mixpanel from 'mixpanel-browser';
import React, { Component } from 'react';
import 'url-search-params-polyfill';
import '../App.css';
import './SiteSolve.css';
import logo from '../assets/img/sslv-logo.png';
import city from '../assets/img/cities.svg';

import ErrorMessage from '../components/ErrorMessage';
import FrameSession from '../components/FrameSession';
import Rating from '../components/Rating';
import Slideshow from '../components/Slideshow/Slideshow';
import SpinnerMessage from '../components/SpinnerMessage';
import * as consts from '../constants';
import getFrameToken from '../getFrameToken';

import {
  SiteSolveFirst,
  SiteSolveSecond,
  SiteSolveThird,
} from '../components/exportImages';
import { Header } from '../components/UI';

class SiteSolve extends Component {
  constructor() {
    super();

    this.state = {
      mode: consts.modes.spinner,
      status: 'Getting ready...',
      email: '',
      userToken: '',
      backend: null,
      hubRedirectUrl: null,
      frameToken: null,
      appId: '',
      launchpadId: '',
    };
  }

  async componentDidMount() {
    // Grab the URL parameters
    const url = new URL(document.URL);
    const params = new URLSearchParams(url.search);

    // The hub will send us this so we can correctly attribute the
    // end-of-session ratings.
    // The email is also our identifier for MixPanel and Frame, so get it as early as
    // possible.
    const user = params.get('email');
    if (user) {
      const decodedEmail = decodeURIComponent(user);
      this.setState({ email: decodedEmail });
      mixpanel.identify(decodedEmail);
    } else {
      // Each unknown user counts as a unique user in Mixpanel which costs us
      // extra, so until we get the email log all analytics under the "unknown"
      // user.
      mixpanel.identify('unknown');
    }

    // Read the backend from the parameters
    let backend = params.get('backend');
    if (backend) {
      this.setState({ backend });
    } else {
      backend = 'vu';
    }

    // CBA to set up a service to read this stuff from the Frame Admin API, so going to hardcode it
    if (backend === 'vu') {
      this.setState({
        launchpadId: 'cc73260c-9fb9-4d14-a8f5-b4b615524b8f.ab311140-2c8e-46ed-a54f-c228702d5299',
        appId: '0228c536-4826-459c-abbe-36e18311e161',
      });
    } else {
      this.setState({
        launchpadId: '2578cf6a-d3d5-4354-8a37-6e1abbfe69a6.056d7424-73a5-43bb-8184-8738cdda8be6',
        appId: '477d671e-0207-4e1d-9bb9-a2f3c4776b53',
      });
    }

    // Get the auth0 code that we'll parse into the app
    const code = params.get('code');
    if (code) {
      this.hubLogin(code);
    } else {
      this.setState({
        mode: consts.modes.loginDisallowed,
        userToken: '',
      });
    }
  }

  hubLogin = (code) => {
    // Remove the auth0 extras from our URL and store it
    const url = new URL(document.URL);
    url.searchParams.delete('code');
    url.searchParams.delete('state');

    this.showSpinner(consts.loggedIn);

    this.setState({
      userToken: code,
      hubRedirectUrl: url,
    },
    // This needs the email state value to be updated, so wait until that happens
    this.retrieveTokenAndStartFrameSession);
  }

  setMode = (mode, status) => {
    this.setState({ mode, status });
  }

  setupUserData = () => {
    const {
      userToken,
      backend,
      hubRedirectUrl,
    } = this.state;

    let userData = '{ "error": "Missing Auth Token" }';
    if (userToken) {
      // Used by the hub to control how the app launches
      const url = new URL(document.URL);
      const project = url.searchParams.get('projectId');
      const file = url.searchParams.get('file');

      const payload = {
        code: userToken,
        redirectUrl: hubRedirectUrl,
        backend,
        project,
        file,
      };
      userData = JSON.stringify(payload);
    }
    return userData;
  }

  retrieveTokenAndStartFrameSession = async () => {
    // Time token retrieval
    mixpanel.time_event(consts.eventNames.getToken);

    let frameToken = null;
    let message = '';
    try {
      const { email } = this.state;
      frameToken = await getFrameToken(email);
      // The FrameSession component will handle the rest
      this.setState({ mode: consts.modes.session, frameToken });
    } catch (err) {
      this.showError(`Couldn't authorise cloud session: ${err.message}`);
      message = err.message;
    }

    // Analytics
    mixpanel.track(consts.eventNames.getToken, {
      success: frameToken != null,
      message,
    });
    message = '';
  }

  showSpinner = (message) => {
    this.setState({
      mode: consts.modes.spinner,
      status: message,
    });
  }

  showError = (message) => {
    this.setState({
      mode: consts.modes.error,
      status: message,
    });
  }

  render() {
    const {
      status,
      mode,
      email,
      frameToken,
      launchpadId,
      appId,
    } = this.state;

    return (
      <div className="App SiteSolve-App">
        <div className="content-area">
          <div className="app-header">
            <img src={city} alt="A stylised cityscape" style={{ width: '376px' }} />
            <img src={logo} alt="The SiteSolve logo" style={{ padding: '32px 0', width: '319px' }} />
          </div>

          {
            {
              error: (<ErrorMessage message={status} />),

              spinner: (
                <>
                  <h3 className="app-title">
                    Welcome to SiteSolve Cloud
                  </h3>
                  <SpinnerMessage message={status} />
                </>
              ),

              session: (
                <>
                  <FrameSession
                    authToken={frameToken}
                    launchpadId={launchpadId}
                    appId={appId}
                    userData={this.setupUserData()}
                    setMode={this.setMode}
                    appName="SiteSolve"
                  />
                </>
              ),

              rating: (
                /* TODO: Extract email of login based on ident */
                <Rating
                  appName="SiteSolve"
                  email={email || ''}
                  setMode={this.setMode}
                />
              ),

              loginDisallowed: (
                <Header size="5" fontWeight="400" color="#f6f6f6">
                  It is no longer possible to login via this page.<br />
                  Please return to <a style={{ fontWeight: 500, color: '#f6f6f6', textDecoration: 'none' }} href="https://hub.vu.city/">the hub</a> to access VU.CITY Cloud.
                </Header>
              ),
            }[mode]
          }
        </div>
        <div className="slide-show">
          <Slideshow slides={[SiteSolveFirst, SiteSolveSecond, SiteSolveThird]} />
        </div>
      </div>
    );
  }
}

SiteSolve.propTypes = {};

export default SiteSolve;
